import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
} from 'react';
import { Branch } from '../../dorian-shared/types/branch/Branch';
import { FEATURE_NODE_EDIT_SIDE_PANEL } from '../../helpers/featureFlags';
import { patchBranchLocation } from '../../helpers/applyLocationsToBranches';
import { useBookLocationsContext } from '../BookLocationsContext/BookLocationsContext';
import {
  CursorPosition,
  CursorState,
  cursorReducer,
} from './cursorReducer';

// Shown at most once per page load — same discoverability pattern as Redux DevTools hint.
let hintShown = false;

export interface CursorContextValue {
  cursor: CursorPosition;
  historyBack: CursorPosition[];
  historyForward: CursorPosition[];
  /** Live unsaved node snapshot. null until the first field edit after NODE_OPENED. */
  draft: Branch | null;
  goBack: () => void;
  // Dispatched on intro (story start) where back/forward history is meaningless.
  resetNavigationHistory: () => void;
  /** Pass `computeForwardFallback(currentNode, cursor)` as `fallback`.
   *  Pass null to express that the button is disabled (no dispatch occurs). */
  goForward: (fallback: CursorPosition | null) => void;
  setStep: (stepIndex: number) => void;
  navigate: (branchId: number, stepIndex?: number | null) => void;
  stepEdited: (branchId: number, stepIndex: number, node: Branch) => void;
  stepAdded: (branchId: number, stepIndex: number, node: Branch) => void;
  stepMoved: (branchId: number, fromIndex: number, toIndex: number, node: Branch) => void;
  stepsDeleted: (branchId: number, stepIndices: number[], node: Branch) => void;
  stepFocused: (branchId: number, stepIndex: number) => void;
  nodeEdited: (branchId: number, node: Branch) => void;
  nodeOpened: (branchId: number, stepIndex: number | null) => void;
  /** branchId is passed for symmetry with nodeOpened and for log traceability.
   *  cacheStepsCount: steps_count from the server cache BEFORE the form was opened.
   *  Used to clamp cursor.stepIndex when an addStep during editing moved the cursor
   *  past the range that the cache knows about. */
  nodeClosed: (branchId: number, cacheStepsCount: number | null) => void;
}

const CursorContext = createContext<CursorContextValue | null>(null);

interface CursorProviderProps {
  initialCursor: CursorPosition;
  children: React.ReactNode;
}

export function CursorProvider({ initialCursor, children }: CursorProviderProps) {
  const initialState: CursorState = {
    cursor: initialCursor,
    historyBack: [],
    historyForward: [],
    draft: null,
  };

  const [state, dispatch] = useReducer(
    // debug tap — remove when the renderState channel lands
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    typeof window !== 'undefined' && (window as any).__CURSOR_DEBUG__
      ? (s: CursorState, a: Parameters<typeof cursorReducer>[1]) => {
        const next = cursorReducer(s, a);
        if (next !== s) {
          // eslint-disable-next-line no-console
          console.log('[cursor-debug]', a.type, 'stepIndex', s.cursor.stepIndex, '→', next.cursor.stepIndex, 'branchId', s.cursor.branchId, '→', next.cursor.branchId, a);
        }
        return next;
      }
      : cursorReducer,
    initialState,
  );

  useEffect(() => {
    if (!FEATURE_NODE_EDIT_SIDE_PANEL) return;
    if (hintShown) return;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    if ((window as any).__CURSOR_DEBUG__) return;
    hintShown = true;
    // eslint-disable-next-line no-console
    console.log('[cursor] debug tap available — run: window.__CURSOR_DEBUG__ = true');
  }, []);

  const goBack = useCallback(() => {
    dispatch({ type: 'GO_BACK' });
  }, []);

  const resetNavigationHistory = useCallback(() => {
    dispatch({ type: 'RESET_NAVIGATION_HISTORY' });
  }, []);

  const goForward = useCallback((fallback: CursorPosition | null) => {
    dispatch({ type: 'GO_FORWARD', fallback: fallback ?? undefined });
  }, []);

  const setStep = useCallback((stepIndex: number) => {
    dispatch({ type: 'SET_STEP', stepIndex });
  }, []);

  const navigate = useCallback((branchId: number, stepIndex?: number | null) => {
    dispatch({ type: 'NAVIGATE', branchId, stepIndex });
  }, []);

  const stepEdited = useCallback((branchId: number, stepIndex: number, node: Branch) => {
    dispatch({
      type: 'STEP_EDITED', branchId, stepIndex, node,
    });
  }, []);

  const stepAdded = useCallback((branchId: number, stepIndex: number, node: Branch) => {
    dispatch({
      type: 'STEP_ADDED', branchId, stepIndex, node,
    });
  }, []);

  const stepMoved = useCallback((branchId: number, fromIndex: number, toIndex: number, node: Branch) => {
    dispatch({
      type: 'STEP_MOVED', branchId, fromIndex, toIndex, node,
    });
  }, []);

  const stepsDeleted = useCallback((branchId: number, stepIndices: number[], node: Branch) => {
    dispatch({
      type: 'STEPS_DELETED', branchId, stepIndices, node,
    });
  }, []);

  const stepFocused = useCallback((branchId: number, stepIndex: number) => {
    dispatch({ type: 'STEP_FOCUSED', branchId, stepIndex });
  }, []);

  const nodeEdited = useCallback((branchId: number, node: Branch) => {
    dispatch({ type: 'NODE_EDITED', branchId, node });
  }, []);

  const nodeOpened = useCallback((branchId: number, stepIndex: number | null) => {
    dispatch({ type: 'NODE_OPENED', branchId, stepIndex });
  }, []);

  const nodeClosed = useCallback((branchId: number, cacheStepsCount: number | null) => {
    dispatch({ type: 'NODE_CLOSED', branchId, cacheStepsCount });
  }, []);

  const value = useMemo<CursorContextValue>(() => ({
    cursor: state.cursor,
    historyBack: state.historyBack,
    historyForward: state.historyForward,
    draft: state.draft,
    goBack,
    resetNavigationHistory,
    goForward,
    setStep,
    navigate,
    stepEdited,
    stepAdded,
    stepMoved,
    stepsDeleted,
    stepFocused,
    nodeEdited,
    nodeOpened,
    nodeClosed,
  }), [
    state.cursor,
    state.historyBack,
    state.historyForward,
    state.draft,
    goBack,
    resetNavigationHistory,
    goForward,
    setStep,
    navigate,
    stepEdited,
    stepAdded,
    stepMoved,
    stepsDeleted,
    stepFocused,
    nodeEdited,
    nodeOpened,
    nodeClosed,
  ]);

  return (
    <CursorContext.Provider value={value}>
      {children}
    </CursorContext.Provider>
  );
}

export function useCursor(): CursorContextValue {
  const ctx = useContext(CursorContext);
  if (!ctx) {
    throw new Error('useCursor must be used within a CursorProvider');
  }
  return ctx;
}

// Autonomous surfaces (ShareLink /play, detach window) render Preview without a
// provider — absence of the channel means autonomous mode, not an error.
export function useOptionalCursor(): CursorContextValue | null {
  return useContext(CursorContext);
}

/** Keep the in-progress node draft's location title/image in sync with the catalog. */
export function LocationsDraftSync() {
  const { bookLocations } = useBookLocationsContext();
  const cursor = useOptionalCursor();

  useEffect(() => {
    if (!cursor || !bookLocations || !cursor.draft) {
      return;
    }
    const patched = patchBranchLocation(cursor.draft, bookLocations);
    if (patched !== cursor.draft) {
      cursor.nodeEdited(cursor.draft.id, patched);
    }
  }, [bookLocations, cursor]);

  return null;
}
